"use client";

import { useCallback, useEffect, useState } from "react";
import { FiCrop, FiRotateCw, FiZoomIn } from "react-icons/fi";
import { LuFlipHorizontal } from "react-icons/lu";

import { Button, Input } from "@/components/ui";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import Tooltip from "@/components/ui/tooltip";

import { cx } from "@/lib/tailwind-utils";

const ZOOM_STOPS = [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 5, 8, 10, 15, 20];
const ZOOM_LABEL_STOPS = [0.25, 0.5, 1, 2, 5, 10, 20];

const ROTATION_STOPS = [-180, -135, -90, -45, 0, 45, 90, 135, 180];
const ROTATION_LABEL_STOPS = [-180, -90, 0, 90, 180];

/** Half the slider thumb width — used to offset ticks so they align with thumb center */
const THUMB_HALF = 7;

/**
 * Compute CSS `left` that aligns a tick with the range input thumb center.
 * Formula: frac * 100% + THUMB_HALF * (1 - 2 * frac) px
 * At frac=0 → 7px, frac=0.5 → 50%, frac=1 → calc(100% - 7px)
 */
function tickLeft(frac: number) {
  const pct = frac * 100;
  const px = THUMB_HALF * (1 - 2 * frac);
  // Round to avoid sub-pixel jitter from long decimals
  const pctR = Math.round(pct * 100) / 100;
  const pxR = Math.round(px * 100) / 100;
  if (Math.abs(pxR) < 0.01) return `${pctR}%`;
  return `calc(${pctR}% + ${pxR}px)`;
}

/** Slider with tick marks and labels aligned to the thumb travel range */
function TickSlider({
  min,
  max,
  step,
  value,
  onChange,
  stops,
  labelStops,
  formatLabel,
  valueToPosition,
}: {
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (value: number) => void;
  stops: number[];
  labelStops: number[];
  formatLabel: (stop: number) => string;
  /** Map a stop value to a 0-1 fraction for tick placement. Defaults to linear. */
  valueToPosition?: (v: number) => number;
}) {
  const toFrac = valueToPosition ?? ((v: number) => (v - min) / (max - min));

  return (
    <div className="relative flex-1">
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="h-1.5 w-full"
      />
      {/* Tick marks */}
      <div className="pointer-events-none relative mt-0.5 h-2 w-full">
        {stops.map((stop) => (
          <div
            key={stop}
            className="absolute -translate-x-px"
            style={{ left: tickLeft(toFrac(stop)) }}
          >
            <div className="h-1.5 w-px bg-border" />
          </div>
        ))}
      </div>
      {/* Labels */}
      <div className="pointer-events-none relative h-3 w-full">
        {labelStops.map((stop) => (
          <span
            key={stop}
            className="absolute -translate-x-1/2 text-micro leading-none text-foreground-muted"
            style={{ left: tickLeft(toFrac(stop)) }}
          >
            {formatLabel(stop)}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Number input that allows clearing the field before typing a new value. Commits on blur or Enter. */
function EditableNumberInput({
  value,
  onCommit,
  min,
  max,
  step,
  className,
}: {
  value: number;
  onCommit: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
}) {
  const [localValue, setLocalValue] = useState(String(value));
  const [focused, setFocused] = useState(false);

  // Sync from parent when not focused
  useEffect(() => {
    if (!focused) setLocalValue(String(value));
  }, [value, focused]);

  const commit = () => {
    const num = parseFloat(localValue);
    if (!isNaN(num)) {
      const clamped =
        min != null && max != null ? Math.max(min, Math.min(num, max)) : num;
      onCommit(clamped);
      setLocalValue(String(Math.round(clamped)));
    } else {
      // Revert to current value
      setLocalValue(String(value));
    }
  };

  return (
    <Input
      type="number"
      min={min}
      max={max}
      step={step}
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          commit();
          (e.target as HTMLInputElement).blur();
        }
      }}
      className={className}
    />
  );
}

export interface ImageTransformState {
  zoom: number;
  rotation: number;
  cropMode?: boolean;
  mirror?: boolean;
}

interface ImageTransformControlsProps {
  // Zoom
  zoom: number;
  onZoomChange: (zoom: number) => void;
  minZoom?: number;
  maxZoom?: number;

  // Rotation
  rotation: number;
  onRotationChange: (rotation: number) => void;
  rotationStep?: number;

  // Crop mode
  cropMode?: boolean;
  onCropModeChange?: (enabled: boolean) => void;
  showCropMode?: boolean;

  // Mirror
  mirror?: boolean;
  onMirrorChange?: (enabled: boolean) => void;
  showMirror?: boolean;

  // Reset
  onReset?: () => void;
  showReset?: boolean;
  hasChanges?: boolean;

  // General
  disabled?: boolean;
  size?: "sm" | "default";
  className?: string;
}

export function ImageTransformControls({
  // Zoom
  zoom,
  onZoomChange,
  minZoom = 0.1,
  maxZoom = 20,

  // Rotation
  rotation,
  onRotationChange,

  // Crop mode
  cropMode = false,
  onCropModeChange,
  showCropMode = true,

  // Mirror
  mirror = false,
  onMirrorChange,
  showMirror = false,

  // Reset
  onReset,
  showReset = true,
  hasChanges = false,

  // General
  disabled = false,
  size = "default",
  className,
}: ImageTransformControlsProps) {
  // Default sizing matches the annotator's tool buttons so the two rows line up.
  const buttonClass =
    size === "sm"
      ? "h-7 w-7 p-0 pointer-coarse:h-11 pointer-coarse:w-11"
      : "h-8 w-8 p-0 pointer-coarse:h-11 pointer-coarse:w-11";
  const labelButtonClass =
    size === "sm"
      ? "h-7 px-1.5 text-caption pointer-coarse:h-11 pointer-coarse:px-3 pointer-coarse:text-body"
      : "h-8 px-2 text-body pointer-coarse:h-11 pointer-coarse:px-3";
  // shrink-0: the base Button's md:px-3 leaves an icon-only button too narrow
  // for its icon, which would otherwise be squeezed.
  const iconClass =
    size === "sm"
      ? "h-3.5 w-3.5 shrink-0"
      : "h-4 w-4 shrink-0 pointer-coarse:h-5 pointer-coarse:w-5";
  const dividerClass =
    size === "sm" ? "mx-1 h-4 w-px bg-border" : "mx-1.5 h-6 w-px bg-border";

  // Magnetically snap zoom in slider-space for consistent snap zones across the range.
  // Each stop occupies 1 segment out of (ZOOM_STOPS.length - 1), so ±1 slider unit is
  // roughly the same visual distance everywhere on the track.
  const SNAP_SLIDER_THRESHOLD = 1; // ±1 out of 0-100 slider range

  // Convert zoom value to slider position (0-100) using ZOOM_STOPS
  const zoomToSlider = useCallback((z: number) => {
    for (let i = 0; i < ZOOM_STOPS.length - 1; i++) {
      if (z <= ZOOM_STOPS[i + 1]) {
        const segmentStart = (i / (ZOOM_STOPS.length - 1)) * 100;
        const segmentEnd = ((i + 1) / (ZOOM_STOPS.length - 1)) * 100;
        const t = (z - ZOOM_STOPS[i]) / (ZOOM_STOPS[i + 1] - ZOOM_STOPS[i]);
        return segmentStart + t * (segmentEnd - segmentStart);
      }
    }
    return 100;
  }, []);

  // Convert slider position (0-100) to zoom value using ZOOM_STOPS
  const sliderToZoom = useCallback((s: number) => {
    const idx = (s / 100) * (ZOOM_STOPS.length - 1);
    const lower = Math.floor(idx);
    const upper = Math.min(lower + 1, ZOOM_STOPS.length - 1);
    const t = idx - lower;
    return ZOOM_STOPS[lower] + t * (ZOOM_STOPS[upper] - ZOOM_STOPS[lower]);
  }, []);

  const zoomPercent = Math.round(zoom * 100);
  const isDisabled = disabled || cropMode;

  return (
    <div className={cx("flex items-center gap-1", className)}>
      {/* Zoom popover */}
      <Popover>
        <Tooltip content="Zoom" triggerAsChild>
          <PopoverTrigger asChild>
            <Button
              variant={zoom !== 1 ? "secondary" : "ghost"}
              disabled={isDisabled}
              aria-label={`Zoom ${zoomPercent}%`}
              className={cx("flex items-center gap-1", labelButtonClass)}
            >
              <FiZoomIn className={iconClass} />
              <span className="text-caption">{zoomPercent}%</span>
            </Button>
          </PopoverTrigger>
        </Tooltip>
        <PopoverContent
          align="center"
          side="bottom"
          className="z-[9999] w-80 p-3"
        >
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-3">
              <TickSlider
                min={0}
                max={100}
                step={0.1}
                value={zoomToSlider(zoom)}
                onChange={(v) => {
                  // Check each stop's slider position for magnetic snap
                  for (const stop of ZOOM_STOPS) {
                    const stopPos = zoomToSlider(stop);
                    if (Math.abs(v - stopPos) <= SNAP_SLIDER_THRESHOLD) {
                      onZoomChange(Math.max(minZoom, Math.min(stop, maxZoom)));
                      return;
                    }
                  }
                  onZoomChange(
                    Math.max(minZoom, Math.min(sliderToZoom(v), maxZoom)),
                  );
                }}
                stops={ZOOM_STOPS}
                labelStops={ZOOM_LABEL_STOPS}
                formatLabel={(s) => `${Math.round(s * 100)}%`}
                valueToPosition={(v) => zoomToSlider(v) / 100}
              />
              <EditableNumberInput
                value={zoomPercent}
                onCommit={(num) =>
                  onZoomChange(Math.max(minZoom, Math.min(num / 100, maxZoom)))
                }
                min={Math.round(minZoom * 100)}
                max={Math.round(maxZoom * 100)}
                step={25}
                className="h-7 w-16 shrink-0 text-center text-caption pointer-coarse:h-11 pointer-coarse:!text-body-lg"
              />
            </div>
            {zoom !== 1 && (
              <div className="mt-1 flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onZoomChange(1)}
                  className="h-6 px-2 text-caption pointer-coarse:h-11 pointer-coarse:px-4 pointer-coarse:text-body"
                >
                  Reset
                </Button>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Rotation popover */}
      <Popover>
        <Tooltip content="Rotate" triggerAsChild>
          <PopoverTrigger asChild>
            <Button
              variant={rotation !== 0 ? "secondary" : "ghost"}
              disabled={isDisabled}
              aria-label={`Rotate ${Math.round(rotation)}°`}
              className={cx("flex items-center gap-1", labelButtonClass)}
            >
              <FiRotateCw className={iconClass} />
              <span className="text-caption">{Math.round(rotation)}°</span>
            </Button>
          </PopoverTrigger>
        </Tooltip>
        <PopoverContent
          align="center"
          side="bottom"
          className="z-[9999] w-80 p-3"
        >
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-3">
              <TickSlider
                min={-180}
                max={180}
                step={1}
                value={Math.round(rotation)}
                onChange={onRotationChange}
                stops={ROTATION_STOPS}
                labelStops={ROTATION_LABEL_STOPS}
                formatLabel={(s) => `${s}°`}
                valueToPosition={(v) => (v + 180) / 360}
              />
              <EditableNumberInput
                value={Math.round(rotation)}
                onCommit={onRotationChange}
                min={-360}
                max={360}
                step={1}
                className="h-7 w-16 shrink-0 text-center text-caption pointer-coarse:h-11 pointer-coarse:!text-body-lg"
              />
            </div>
            {rotation !== 0 && (
              <div className="mt-1 flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRotationChange(0)}
                  className="h-6 px-2 text-caption pointer-coarse:h-11 pointer-coarse:px-4 pointer-coarse:text-body"
                >
                  Reset
                </Button>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Crop mode toggle */}
      {showCropMode && onCropModeChange && (
        <Tooltip
          content={cropMode ? "Exit crop mode" : "Crop image"}
          triggerAsChild
        >
          <Button
            variant={cropMode ? "secondary" : "ghost"}
            onClick={() => onCropModeChange(!cropMode)}
            disabled={disabled}
            aria-label={cropMode ? "Exit crop mode" : "Crop image"}
            className={cx("flex items-center justify-center", buttonClass)}
          >
            <FiCrop className={iconClass} />
          </Button>
        </Tooltip>
      )}

      {/* Mirror toggle */}
      {showMirror && onMirrorChange && (
        <Tooltip content="Mirror image" triggerAsChild>
          <Button
            variant={mirror ? "secondary" : "ghost"}
            onClick={() => onMirrorChange(!mirror)}
            disabled={disabled}
            aria-label="Mirror image"
            className={cx("flex items-center justify-center", buttonClass)}
          >
            <LuFlipHorizontal className={iconClass} />
          </Button>
        </Tooltip>
      )}

      {/* Reset all button */}
      {showReset && onReset && (
        <>
          <div className={dividerClass} />
          <Button
            variant="ghost"
            onClick={onReset}
            disabled={disabled || !hasChanges}
            className={cx(
              "flex items-center px-2",
              size === "sm"
                ? "h-7 text-caption pointer-coarse:h-11 pointer-coarse:px-4 pointer-coarse:text-body"
                : "h-8 text-body pointer-coarse:h-11 pointer-coarse:px-4",
            )}
            title="Reset All"
          >
            <span className="whitespace-nowrap">Reset All</span>
          </Button>
        </>
      )}
    </div>
  );
}
